import cn from 'classnames';
import { Avatar, Button, lucideIcons, Skeleton, Tooltip } from '@round/creatorbase-ui';
import { CopyButton } from '@round/creatorbase-helpers/components';
import { getInitials, getProfileUrl } from '../../helpers';
import styles from './ResultCreator.module.css';

const { ChartSpline: DiagramIcon } = lucideIcons;

type Props = {
    username: string;
    avatarUrl: string | undefined;
    /** Avatars are fetched a request behind the rows themselves. */
    isAvatarLoading: boolean;
    className?: string;
    /** Only set once the creator has been added to the client's base - null until then. */
    clientInfluencerAccountId?: number | null;
    /** Omitted where opening the creator details modal doesn't apply. */
    onOpenDetails?: (clientInfluencerAccountId: number) => void;
};

/** How a result names its creator, in the results table and in the campaign modal. */
export const ResultCreator = ({
    username,
    avatarUrl,
    isAvatarLoading,
    className,
    clientInfluencerAccountId,
    onOpenDetails,
}: Props) => {
    const handle = `@${username}`;

    return (
        <div className={cn(styles.container, className)}>
            {/* Loading a later page must not put the avatars already resolved back behind a placeholder. */}
            {isAvatarLoading && !avatarUrl ? (
                <Skeleton className={styles.avatarSkeleton} />
            ) : (
                <Avatar size="sm" className={styles.avatar}>
                    <Avatar.Image className={styles.avatarImage} src={avatarUrl} alt="" />
                    <Avatar.Fallback className={styles.avatarFallback}>{getInitials(username)}</Avatar.Fallback>
                </Avatar>
            )}

            <a className={styles.username} href={getProfileUrl(username)} target="_blank" rel="noreferrer">
                {handle}
            </a>

            <CopyButton
                className={styles.copyButton}
                value={username}
                variant="ghost"
                size="icon-sm"
                tooltipLabel="Copy handle"
                aria-label={`Copy ${handle}`}
            />

            {typeof clientInfluencerAccountId === 'number' && onOpenDetails && (
                <Tooltip delayDuration={300}>
                    <Tooltip.Trigger asChild>
                        <Button
                            className={styles.detailsButton}
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`View ${handle} details`}
                            onClick={() => onOpenDetails(clientInfluencerAccountId)}
                        >
                            <DiagramIcon />
                        </Button>
                    </Tooltip.Trigger>
                    <Tooltip.Content>Creator details</Tooltip.Content>
                </Tooltip>
            )}
        </div>
    );
};
